import { inject, Injectable, Inject, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { first } from 'rxjs';
import { CheckoutObject } from '../../models/checkout';
import { AuthService } from '../auth/auth.service';
import { BaseViewConfigService } from '../base-view-config/base-view-config.service';
import { NavigationConfig } from '../../models/navigation-config';
import { PriceService } from '../price/price.service';
import { CartListCheckoutConfigService } from '../cart-list-checkout-config/cart-list-checkout-config.service';
import { gungGetPropertyOfObject } from '../../utils/gung-utils';
import { ClerkSearchService } from '../clerk-search/clerk-search.service';

@Injectable({
  providedIn: 'root'
})
export class GungClerkIoService {
  private readonly baseUrl = 'https://api.clerk.io/v2';
  private http = inject(HttpClient);
  private baseViewConfigService = inject(BaseViewConfigService);
  private authService = inject(AuthService);
  private priceService = inject(PriceService);
  private cartListCheckoutConfigService = inject(CartListCheckoutConfigService);
  private clerkSearchService = inject(ClerkSearchService);
  private userEmail: string | undefined;

  constructor(@Optional() @Inject('environment') private environment: NavigationConfig) {
    this.authService.getCurrentUser().subscribe(user => {
      this.userEmail = user?.email;
      if (user?.email) {
        this.logEmail(user.email);
      }
    });
  }

  private logEmail(email: string): void {
    if (!this.enabled()) return;
    this.http.get(`${this.baseUrl}/log/email`, {
      params: { key: this.apiKey, email, visitor: this.clerkSearchService.getVisitorId() }
    }).subscribe();
  }

  private get apiKey(): string {
    return this.environment?.clerkApiKey || '';
  }

  private enabled(): boolean {
    return this.baseViewConfigService.enableClerkEvents && !!this.apiKey;
  }

  private get identity(): { visitor: string; email?: string } {
    const visitor = this.clerkSearchService.getVisitorId();
    return this.userEmail ? { visitor, email: this.userEmail } : { visitor };
  }

  logProduct(productId: string): void {
    if (!this.enabled()) return;
    this.http.get(`${this.baseUrl}/log/product`, {
      params: { key: this.apiKey, product: productId, ...this.identity }
    }).subscribe();
  }

  logCategory(categoryId: string): void {
    if (!this.enabled()) return;
    this.http.get(`${this.baseUrl}/log/category`, {
      params: { key: this.apiKey, category: categoryId, ...this.identity }
    }).subscribe();
  }

  cartAdd(productId: string, qty: number): void {
    if (!this.enabled()) return;
    this.http.get(`${this.baseUrl}/log/cart/add`, {
      params: {
        key: this.apiKey,
        products: JSON.stringify([{ id: productId, quantity: qty }]),
        ...this.identity
      }
    }).subscribe();
  }

  cartRemove(productId: string): void {
    if (!this.enabled()) return;
    this.http.get(`${this.baseUrl}/log/cart/remove`, {
      params: {
        key: this.apiKey,
        products: JSON.stringify([{ id: productId }]),
        ...this.identity
      }
    }).subscribe();
  }

  cartUpdate(productId: string, qty: number): void {
    if (!this.enabled()) return;
    this.http.get(`${this.baseUrl}/log/cart/update`, {
      params: {
        key: this.apiKey,
        products: JSON.stringify([{ id: productId, quantity: qty }]),
        ...this.identity
      }
    }).subscribe();
  }

  logSale(checkout: CheckoutObject, orderId: string): void {
    if (!this.enabled() || !orderId) return;
    const ids = checkout.rows.map(r => r.id);
    this.priceService.getCurrentCustomerPrices(ids).pipe(first()).subscribe(prices => {
      const priceField = this.cartListCheckoutConfigService.changePriceField;
      const discountField = this.cartListCheckoutConfigService.changeDiscountField;
      this.http.post(`${this.baseUrl}/log/sale`, {
        key: this.apiKey,
        sale: orderId,
        products: checkout.rows.map(r => {
          const customerPrice = prices.find(p => p.productId === r.id);
          if (!customerPrice) {
            return { id: r.id, quantity: r.quantity };
          }
          const overridePrice = gungGetPropertyOfObject(priceField, r.extra);
          const overrideDiscount = gungGetPropertyOfObject(discountField, r.extra);
          const basePrice = overridePrice != null ? overridePrice : customerPrice.customerNetPrice.value;
          const netPrice = overrideDiscount != null ? basePrice * (1 - overrideDiscount / 100) : basePrice;
          return { id: r.id, quantity: r.quantity, price: netPrice };
        }),
        ...this.identity
      }).subscribe();
    });
  }
}
